import React from 'react';
import {useNavigate} from "react-router-dom";
import {useAuth} from "../authentication/AuthContext";

export default function CompitoLandingPage() {
    const navigate = useNavigate()
    const {user} = useAuth()
    const isAuthenticated = Boolean(user?.email)
    const primaryCtaLabel = isAuthenticated ? 'Open Dashboard' : 'Start for Free'
    const primaryCtaPath = isAuthenticated ? '/home' : '/signup'

    const productAreas = [
        {
            title: 'Recall Cards',
            description: 'Turn notes into active recall cards so key ideas move from short-term reading to long-term memory.',
        },
        {
            title: 'Projects',
            description: 'Organize outcomes and milestones in one place so your learning and execution stay aligned.',
        },
        {
            title: 'Notes',
            description: 'Capture insights, summarize concepts, and build a structured personal knowledge base over time.',
        },
        {
            title: 'Tasks',
            description: 'Break plans into actionable steps and prioritize what should be done next.',
        },
        {
            title: 'Focus Sessions',
            description: 'Use focused work sessions to protect deep work and reduce context switching.',
        },
        {
            title: 'Analytics',
            description: 'See streaks, completion, and learning momentum at a glance.',
        },
    ];

    const coreWorkflow = [
        {
            title: 'Capture',
            description: 'Save notes, ideas, and resources.',
        },
        {
            title: 'Organize',
            description: 'Turn them into projects and actionable tasks.',
        },
        {
            title: 'Focus',
            description: 'Work through tasks using structured sessions.',
        },
        {
            title: 'Remember',
            description: 'Convert important knowledge into recall cards.',
        },
        {
            title: 'Improve',
            description: 'Track consistency and learning momentum.',
        },
    ];

    const pricingPlans = [
        {
            name: 'Free',
            price: '€0',
            cadence: 'forever',
            description: 'Everything you need to build a focused daily system.',
            features: ['Notes, tasks, and projects', 'Focus sessions', 'Recall cards', 'Personal analytics'],
        },
        {
            name: 'Pro',
            price: '€9',
            cadence: 'per month',
            annualPrice: 'Or €7/month billed annually (€84/year)',
            description: 'More power for learners and builders who want deeper insights.',
            features: ['Everything in Free', 'AI-powered features', 'Advanced analytics', 'Unlimited learning history', 'Priority access to new features'],
            featured: true,
        },
    ];

    const heroWorkflow = [
        {label: 'NOTE', title: 'Capture the key idea', meta: 'Atomic Habits · Chapter 3', color: 'bg-cyan-400'},
        {label: 'TASK', title: 'Put it into practice', meta: 'Today · High priority', color: 'bg-violet-400'},
        {label: 'FOCUS', title: 'Work without distraction', meta: '25 min focus session', color: 'bg-fuchsia-400'},
        {label: 'RECALL', title: 'Make the insight stick', meta: 'Review tomorrow', color: 'bg-emerald-400'},
    ];

    return (
        <div className="font-sans bg-[#111427] text-gray-100">
            <header className="sticky top-0 z-50 border-b border-white/10 bg-[#111427]/90 px-4 backdrop-blur-xl sm:px-6">
                <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-4">
                    <a href="#top" aria-label="Compitino home" className="shrink-0">
                        <img
                            src="/compitino_logo_horizontal_dark.svg"
                            alt="Compitino"
                            className="h-9 w-36 object-contain object-left"
                        />
                    </a>

                    <nav
                        aria-label="Main navigation"
                        className="hidden items-center gap-7 text-sm font-medium text-gray-300 md:flex"
                    >
                        <a className="transition hover:text-white" href="#features">Features</a>
                        <a className="transition hover:text-white" href="#use-cases">How It Works</a>
                        <a className="transition hover:text-white" href="#pricing">Pricing</a>
                    </nav>

                    <div className="flex shrink-0 items-center gap-2 sm:gap-3">
                        {!isAuthenticated && (
                            <button
                                className="px-2 py-2 text-sm font-semibold text-gray-300 transition hover:text-white sm:px-3"
                                onClick={() => navigate('/signin')}
                            >
                                Sign In
                            </button>
                        )}
                        <button
                            className="rounded-lg bg-indigo-600 px-3 py-2 text-sm font-semibold text-white transition hover:-translate-y-0.5 hover:bg-indigo-500 sm:px-4"
                            onClick={() => navigate(primaryCtaPath)}
                        >
                            {primaryCtaLabel}
                        </button>
                    </div>
                </div>

                <nav
                    aria-label="Mobile navigation"
                    className="mx-auto flex max-w-6xl items-center justify-center gap-6 overflow-x-auto border-t border-white/5 py-2 text-xs font-medium text-gray-300 md:hidden"
                >
                    <a className="whitespace-nowrap transition hover:text-white" href="#features">Features</a>
                    <a className="whitespace-nowrap transition hover:text-white" href="#use-cases">How It Works</a>
                    <a className="whitespace-nowrap transition hover:text-white" href="#pricing">Pricing</a>
                </nav>
            </header>

            <section id="top" className="relative scroll-mt-28 overflow-hidden border-b border-white/10 bg-gradient-to-br from-[#1b2040] via-[#1c2250] to-[#141738] px-6 py-20 sm:scroll-mt-16 sm:py-24">
                <div className="absolute -top-24 left-1/3 h-72 w-72 rounded-full bg-fuchsia-500/25 blur-3xl"/>
                <div className="absolute bottom-0 right-0 h-64 w-64 rounded-full bg-cyan-400/20 blur-3xl"/>
                <div className="absolute top-10 right-20 h-44 w-44 rounded-full bg-indigo-500/20 blur-3xl"/>
                <div className="relative mx-auto grid max-w-6xl items-center gap-14 lg:grid-cols-[1.08fr_0.92fr] lg:gap-12">
                    <div>
                        <div className="inline-flex items-center gap-2 rounded-full border border-indigo-200/40 bg-indigo-400/20 px-3 py-1 text-xs font-medium text-indigo-50">
                            The personal operating system for learning and execution
                        </div>

                        <div className="mt-6 flex flex-col gap-6">
                        <h1 className="max-w-4xl text-4xl font-bold leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl">
                            Turn what you learn into what you do.
                        </h1>

                        <p className="text-lg leading-relaxed text-gray-300 sm:text-xl">
                            Compitino connects your notes, tasks, projects, focus sessions, and recall practice in one continuous workflow.
                        </p>


                        <div className="flex flex-wrap gap-3">
                            <button
                                className="rounded-xl bg-indigo-600 px-6 py-3 font-semibold text-white transition hover:-translate-y-0.5 hover:bg-indigo-500"
                                onClick={() => navigate(primaryCtaPath)}
                            >
                                {primaryCtaLabel}
                            </button>
                            {!isAuthenticated && (
                                <a
                                    className="rounded-xl border border-gray-400/60 px-6 py-3 font-semibold text-gray-200 transition hover:-translate-y-0.5 hover:bg-white/10"
                                    href="#use-cases"
                                >
                                    See How It Works
                                </a>
                            )}
                        </div>
                        </div>
                    </div>

                    <div className="relative mx-auto w-full max-w-lg lg:mx-0" aria-label="A connected Compitino workflow from note to recall card">
                        <div className="absolute -inset-8 rounded-full bg-indigo-400/10 blur-3xl"/>
                        <div className="relative rotate-1 rounded-[2rem] border border-white/15 bg-[#11162f]/90 p-3 shadow-[0_30px_80px_rgba(0,0,0,0.45)] backdrop-blur-xl sm:p-5">
                            <div className="flex items-center justify-between border-b border-white/10 px-2 pb-4">
                                <div>
                                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-indigo-200">Connected workflow</p>
                                    <p className="mt-1 text-sm text-gray-400">One idea, moving forward</p>
                                </div>
                                <div className="flex gap-1.5" aria-hidden="true">
                                    <span className="h-2 w-2 rounded-full bg-fuchsia-400/70"/>
                                    <span className="h-2 w-2 rounded-full bg-indigo-400/70"/>
                                    <span className="h-2 w-2 rounded-full bg-cyan-400/70"/>
                                </div>
                            </div>

                            <div className="mt-4 space-y-0">
                                {heroWorkflow.map((item, index) => (
                                    <div key={item.label} className="relative flex gap-3">
                                        <div className="flex w-8 shrink-0 flex-col items-center">
                                            <span className={`mt-5 h-3 w-3 rounded-full ${item.color} shadow-[0_0_16px_currentColor]`}/>
                                            {index < heroWorkflow.length - 1 && (
                                                <span className="h-full min-h-8 w-px bg-gradient-to-b from-indigo-300/60 to-indigo-300/10"/>
                                            )}
                                        </div>
                                        <div className="mb-3 flex-1 rounded-2xl border border-white/10 bg-white/[0.06] p-4 transition hover:-translate-y-0.5 hover:border-indigo-300/30 hover:bg-white/10">
                                            <div className="flex items-center justify-between gap-3">
                                                <span className="text-[10px] font-bold tracking-[0.16em] text-indigo-200">{item.label}</span>
                                                <span className="text-[10px] text-gray-500">0{index + 1}</span>
                                            </div>
                                            <p className="mt-1 text-sm font-semibold text-white">{item.title}</p>
                                            <p className="mt-1 text-xs text-gray-400">{item.meta}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="mt-1 flex items-center justify-between rounded-xl border border-emerald-300/15 bg-emerald-400/10 px-4 py-3 text-xs">
                                <span className="font-medium text-emerald-100">Knowledge turned into momentum</span>
                                <span className="text-emerald-300" aria-hidden="true">✓</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section id="use-cases" className="scroll-mt-28 bg-[#151a35] px-6 py-16 sm:scroll-mt-16 sm:py-20">
                <div className="mx-auto max-w-6xl">
                    <div className="mx-auto max-w-2xl text-center">
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-indigo-300">One continuous loop</p>
                        <h2 className="mt-3 text-3xl font-bold text-white sm:text-4xl">Core Workflow</h2>
                        <p className="mt-3 text-gray-400">Move naturally from a captured idea to focused action and lasting knowledge.</p>
                    </div>

                    <div className="relative mt-12 grid gap-4 md:grid-cols-5 md:gap-3">
                        <div className="absolute left-[10%] right-[10%] top-6 hidden h-px bg-gradient-to-r from-cyan-400/30 via-indigo-400/70 to-emerald-400/30 md:block" aria-hidden="true"/>
                        {coreWorkflow.map((step, index) => (
                            <article key={step.title} className="group relative flex gap-4 rounded-2xl border border-white/10 bg-white/5 p-5 transition hover:-translate-y-1 hover:border-indigo-300/40 hover:bg-white/10 md:block md:border-0 md:bg-transparent md:p-2 md:text-center md:hover:bg-transparent">
                                <div className="relative z-10 inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-indigo-300/30 bg-[#202750] text-sm font-bold text-indigo-100 shadow-[0_0_25px_rgba(99,102,241,0.25)] transition group-hover:border-indigo-300/70 group-hover:bg-indigo-500">
                                    {String(index + 1).padStart(2, '0')}
                                </div>
                                <div className="md:mt-5">
                                    <h3 className="font-semibold text-white">{step.title}</h3>
                                    <p className="mt-2 text-sm leading-relaxed text-gray-400">{step.description}</p>
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            <section id="features" className="scroll-mt-28 border-t border-white/5 px-6 py-16 sm:scroll-mt-16 sm:py-20">
                <div className="mx-auto max-w-6xl">
                    <div className="mb-10 max-w-2xl">
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-cyan-300">Everything stays connected</p>
                        <h2 className="mt-3 text-3xl font-bold text-white sm:text-4xl">A connected workspace for learning and execution</h2>
                        <p className="mt-3 text-gray-400">All the tools you need, working together instead of creating more silos.</p>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {productAreas.map((area) => (
                            <article
                                key={area.title}
                                className="rounded-2xl border border-white/10 bg-white/5 p-5 shadow-[0_12px_30px_rgba(0,0,0,0.25)] transition hover:-translate-y-1 hover:border-indigo-300/50 hover:bg-white/10"
                            >
                                <h3 className="text-lg font-semibold text-white">{area.title}</h3>
                                <p className="mt-2 text-sm leading-relaxed text-gray-300">{area.description}</p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            <section className="px-6 py-16">
                <div className="mx-auto max-w-6xl rounded-3xl border border-indigo-300/20 bg-gradient-to-r from-[#1a1f45] via-[#1f2555] to-[#1a2150] p-6 sm:p-8">
                    <div className="mb-6 flex items-center justify-between gap-4">
                        <h3 className="text-2xl font-bold text-white">Analytics Preview</h3>
                        <span className="rounded-full bg-cyan-400/20 px-3 py-1 text-xs text-cyan-100">Live momentum</span>
                    </div>

                    <div className="grid gap-4 lg:grid-cols-3">
                        <div className="rounded-2xl border border-white/15 bg-[#222a5a] p-4">
                            <p className="text-xs uppercase tracking-wider text-indigo-100">Weekly score</p>
                            <div className="mt-4 flex items-end gap-2">
                                {[45, 62, 58, 76, 84, 71, 89].map((h, idx) => (
                                    <div key={idx} className="flex-1">
                                        <div className="rounded-t-md bg-gradient-to-t from-indigo-500 to-cyan-400" style={{ height: `${h}px` }} />
                                    </div>
                                ))}
                            </div>
                            <p className="mt-3 text-xs text-gray-300">+18% vs last week</p>
                        </div>

                        <div className="rounded-2xl border border-white/15 bg-[#222a5a] p-4">
                            <p className="text-xs uppercase tracking-wider text-indigo-100">Focus consistency</p>
                            <div className="mt-4 space-y-3">
                                <div>
                                    <div className="mb-1 flex items-center justify-between text-xs text-gray-300"><span>Sessions done</span><span>82%</span></div>
                                    <div className="h-2 rounded-full bg-white/10"><div className="h-2 w-[82%] rounded-full bg-gradient-to-r from-fuchsia-400 to-indigo-400"/></div>
                                </div>
                                <div>
                                    <div className="mb-1 flex items-center justify-between text-xs text-gray-300"><span>Task completion</span><span>74%</span></div>
                                    <div className="h-2 rounded-full bg-white/10"><div className="h-2 w-[74%] rounded-full bg-gradient-to-r from-cyan-400 to-indigo-400"/></div>
                                </div>
                                <div>
                                    <div className="mb-1 flex items-center justify-between text-xs text-gray-300"><span>Recall retention</span><span>68%</span></div>
                                    <div className="h-2 rounded-full bg-white/10"><div className="h-2 w-[68%] rounded-full bg-gradient-to-r from-violet-400 to-pink-400"/></div>
                                </div>
                            </div>
                        </div>

                        <div className="rounded-2xl border border-white/15 bg-[#222a5a] p-4">
                            <p className="text-xs uppercase tracking-wider text-indigo-100">Current streaks</p>
                            <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                                <div className="rounded-xl bg-emerald-400/20 p-3 text-emerald-100">
                                    <p className="text-[10px] uppercase">Daily notes</p>
                                    <p className="mt-1 text-lg font-semibold">14d</p>
                                </div>
                                <div className="rounded-xl bg-cyan-400/20 p-3 text-cyan-100">
                                    <p className="text-[10px] uppercase">Focus</p>
                                    <p className="mt-1 text-lg font-semibold">9d</p>
                                </div>
                                <div className="rounded-xl bg-fuchsia-400/20 p-3 text-fuchsia-100">
                                    <p className="text-[10px] uppercase">Recall</p>
                                    <p className="mt-1 text-lg font-semibold">11d</p>
                                </div>
                                <div className="rounded-xl bg-indigo-400/20 p-3 text-indigo-100">
                                    <p className="text-[10px] uppercase">Tasks</p>
                                    <p className="mt-1 text-lg font-semibold">86%</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section id="pricing" className="relative scroll-mt-28 overflow-hidden border-t border-white/10 bg-[#151a35] px-6 py-16 sm:scroll-mt-16 sm:py-20">
                <div className="absolute left-1/2 top-0 h-64 w-64 -translate-x-1/2 rounded-full bg-indigo-500/15 blur-3xl"/>
                <div className="relative mx-auto max-w-5xl">
                    <div className="mx-auto max-w-2xl text-center">
                        <span className="inline-flex rounded-full border border-indigo-300/30 bg-indigo-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-indigo-200">
                            Simple pricing
                        </span>
                        <h2 className="mt-4 text-3xl font-bold text-white sm:text-4xl">Start free. Grow at your pace.</h2>
                        <p className="mt-3 text-gray-300">
                            Build your learning system today—no credit card required.
                        </p>
                    </div>

                    <div className="mx-auto mt-10 grid max-w-4xl gap-5 md:grid-cols-2 md:items-stretch">
                        {pricingPlans.map((plan) => (
                            <article
                                key={plan.name}
                                className={`relative flex flex-col rounded-3xl border p-6 shadow-[0_18px_45px_rgba(0,0,0,0.25)] sm:p-8 ${
                                    plan.featured
                                        ? 'border-indigo-400/70 bg-gradient-to-b from-indigo-500/20 to-white/5'
                                        : 'border-white/10 bg-white/5'
                                }`}
                            >
                                {plan.featured && (
                                    <span className="absolute right-5 top-5 rounded-full bg-indigo-500 px-3 py-1 text-xs font-semibold text-white">
                                        Most popular
                                    </span>
                                )}
                                <p className="text-lg font-semibold text-white">{plan.name}</p>
                                <div className="mt-5">
                                    <span className={`${plan.featured ? 'text-3xl' : 'text-4xl'} font-bold text-white`}>{plan.price}</span>
                                    <p className="mt-1 text-sm text-gray-400">{plan.cadence}</p>
                                    {plan.annualPrice && (
                                        <p className="mt-2 inline-flex rounded-full bg-emerald-400/10 px-3 py-1 text-xs font-medium text-emerald-200">
                                            {plan.annualPrice}
                                        </p>
                                    )}
                                </div>
                                <p className="mt-5 text-sm leading-relaxed text-gray-300">{plan.description}</p>

                                <ul className="mt-6 flex-1 space-y-3">
                                    {plan.features.map((feature) => (
                                        <li key={feature} className="flex items-start gap-3 text-sm text-gray-200">
                                            <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-indigo-400/20 text-xs text-indigo-200" aria-hidden="true">✓</span>
                                            {feature}
                                        </li>
                                    ))}
                                </ul>

                                <button
                                    className={`mt-8 w-full rounded-xl px-5 py-3 font-semibold transition hover:-translate-y-0.5 ${
                                        plan.featured
                                            ? 'bg-indigo-600 text-white hover:bg-indigo-500'
                                            : 'border border-white/20 bg-white/5 text-white hover:bg-white/10'
                                    }`}
                                    onClick={() => navigate(primaryCtaPath)}
                                >
                                    {primaryCtaLabel}
                                </button>
                            </article>
                        ))}
                    </div>

                    <p className="mt-6 text-center text-xs text-gray-500">
                        Save €24 with annual billing. Cancel anytime.
                    </p>
                </div>
            </section>

            <footer className="border-t border-white/10 bg-[#0c1020] py-6 text-center text-sm text-gray-500">
                © {new Date().getFullYear()} Compitino - Learn better, ship faster.
            </footer>
        </div>
    );
}
